import React, { useState } from 'react';
import { useInput, useTranslate, useGetList } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { RATE_TYPE_OPTIONS, DEFAULT_RATE_TYPE } from '../../../constants/feeTemplates';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    FormControlLabel,
    TextField,
    Button,
    Chip,
    Alert,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    ExpandMore,
    Add,
    AttachMoney,
    ElectricBolt,
    Schedule,
    HourglassEmpty,
    EventSeat,
    InfoOutlined,
} from '@mui/icons-material';
import { FeeStructure, EnergyRate, TimeWindow, TimeRate } from '../../../types/fee.types';
import { NumberField } from '../../common/NumberField';

const PolicyDropdown: React.FC<{ value: string | null; onChange: (id: string | null) => void }> = ({ value, onChange }) => {
    const translate = useTranslate();
    const { data, isLoading } = useGetList('reservation-policies', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'name', order: 'ASC' },
    });

    return (
        <TextField
            select
            label={translate('resources.fee-templates.reservationFee.policy')}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
            helperText={translate('resources.fee-templates.reservationFee.policyHelper')}
            size="small"
            fullWidth
        >
            <MenuItem value="">
                <em>{translate('resources.fee-templates.reservationFee.useSystemDefault')}</em>
            </MenuItem>
            {(data || []).map((p: any) => {
                const isDefault = p.is_system_default === true || p.isSystemDefault === true;
                return (
                    <MenuItem key={p.id} value={p.id}>
                        {p.name}{isDefault ? ` (${translate('resources.fee-templates.reservationFee.default')})` : ''}
                    </MenuItem>
                );
            })}
        </TextField>
    );
};

function feePolicyToRefundKey(feePolicy: string): string {
    switch (feePolicy) {
        case 'hold_release':             return 'holdRelease';
        case 'hold_apply':               return 'holdApply';
        case 'hold_forfeit_on_noshow':   return 'holdForfeit';
        case 'none':
        default:                         return 'none';
    }
}

const RefundLabelPreview: React.FC<{ policyId: string | null }> = ({ policyId }) => {
    const translate = useTranslate();
    const { data: policies } = useGetList('reservation-policies', { pagination: { page: 1, perPage: 200 } });
    const isDefault = (p: any) => p.is_system_default === true || p.isSystemDefault === true;
    const target = policyId
        ? policies?.find((p: any) => p.id === policyId)
        : policies?.find(isDefault);
    if (!target) return null;
    const labelKey = `resources.reservations.refundLabel.${feePolicyToRefundKey(target.fee_policy)}`;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {translate('resources.fee-templates.reservationFee.refundBehavior')}:
            </Typography>
            <Chip size="small" label={translate(labelKey)} />
        </Box>
    );
};

interface FeeStructureFormProps {
    source: string;
}

// Custom Time Picker Component
interface CustomTimePickerProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
}

const CustomTimePicker: React.FC<CustomTimePickerProps> = ({ label, value, onChange }) => {
    const [hour, minute] = value.split(':').map(Number);
    
    const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
    const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    
    const handleHourChange = (newHour: string) => {
        onChange(`${newHour}:${minute.toString().padStart(2, '0')}`);
    };
    
    const handleMinuteChange = (newMinute: string) => {
        onChange(`${hour.toString().padStart(2, '0')}:${newMinute}`);
    };
    
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 60 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Hour</InputLabel>
                <Select
                    value={hour.toString().padStart(2, '0')}
                    onChange={(e) => handleHourChange(e.target.value)}
                    label="Hour"
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00C853',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00C853',
                        },
                        '& .MuiSelect-select': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                        },
                    }}
                >
                    {hours.map((h) => (
                        <MenuItem key={h} value={h} sx={{ fontFamily: 'monospace' }}>
                            {h}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>:</Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Min</InputLabel>
                <Select
                    value={minute.toString().padStart(2, '0')}
                    onChange={(e) => handleMinuteChange(e.target.value)}
                    label="Min"
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00C853',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00C853',
                        },
                        '& .MuiSelect-select': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                        },
                    }}
                >
                    {minutes.map((m) => (
                        <MenuItem key={m} value={m} sx={{ fontFamily: 'monospace' }}>
                            {m}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                {label}
            </Typography>
        </Box>
    );
};

const defaultFeeStructure: FeeStructure = {
    version: '1.0',
    currency: 'HKD',
    components: {
        sessionFee: { enabled: false, amount: 0 },
        energyFee: { enabled: false, rates: [] },
        timeFee: { enabled: false, unit: 'minute', rates: [], freeMinutes: 0, maxChargeMinutes: 0 },
        idleFee: { enabled: false, graceMinutes: 15, rate: 1, unit: 'minute' },
        reservationFee: { enabled: false, amount: 0, policyId: null },
    },
    specialDates: [],
    metadata: {},
};

// Energy-XOR-Time toggle reducers (sub-plan 09.1), extracted as pure functions so the
// mutual-exclusion behaviour is unit-testable without rendering the form. They return the
// `updatesByKey` map applied atomically via updateComponents.
//
// Disabling energy PRESERVES its rates so toggling off and on again does not lose the user's
// work; the disabled component's rates are stripped at SAVE (stripDisabledConsumptionRates
// transform) so a "disabled" energy template never bills. Enabling TIME does clear energy rates,
// because energy is presence-based: leaving them would keep energy "active" and the
// (presence-based) XOR validator would block the save with the energy rates hidden behind the
// disabled toggle, leaving the user stuck.
export const computeEnergyToggle = (enabled: boolean): Record<string, any> =>
    enabled
        ? { energyFee: { enabled: true }, timeFee: { enabled: false } }
        : { energyFee: { enabled: false } };

export const computeTimeToggle = (enabled: boolean): Record<string, any> =>
    enabled
        ? { timeFee: { enabled: true }, energyFee: { enabled: false, rates: [] } }
        : { timeFee: { enabled: false } };

export const FeeStructureForm: React.FC<FeeStructureFormProps> = ({ source }) => {
    const translate = useTranslate();
    const {
        field: { value = defaultFeeStructure, onChange },
    } = useInput({ source });
    const formContext = useFormContext();
    const idleFeeErrors = (formContext?.formState?.errors as any)?.feeStructure?.components?.idleFee;
    const resolveErrorText = (fieldError: any): string | undefined => {
        const wrapped = fieldError?.message;
        const key = typeof wrapped === 'string' ? wrapped : wrapped?.message;
        if (typeof key !== 'string') return undefined;
        const args = wrapped && typeof wrapped === 'object' ? wrapped.args : undefined;
        return translate(key, { ...args, _: key });
    };
    const idleRateErrorText = resolveErrorText(idleFeeErrors?.rate);
    const idleGraceErrorText = resolveErrorText(idleFeeErrors?.graceMinutes);

    // Ensure we have a properly initialized value with all required fields.
    // IMPORTANT: never silently coerce numeric pricing fields here — empty/invalid
    // input must surface as a backend validation error so a user does not get
    // their pricing rewritten to 0 or 1 without warning.
    const normalizeFeeStructure = (structure: any) => {
        const normalized = {
            ...defaultFeeStructure,
            ...structure,
            components: {
                ...defaultFeeStructure.components,
                ...structure?.components,
            },
        };

        // idleFee.unit is a literal('minute') — fill ONLY when missing (system-controlled).
        // Do NOT coerce idleFee.rate: an invalid value must reach zod and produce
        // a real validation error rather than a silent 0.
        if (normalized.components.idleFee && !normalized.components.idleFee.unit) {
            normalized.components.idleFee.unit = 'minute';
        }

        // For energyFee rates, only normalise the always-required `days` array
        // inside time windows (a list of ISO weekday names). Pricing values are
        // left as-is so missing/invalid rates are caught by zod.
        if (normalized.components.energyFee?.rates) {
            normalized.components.energyFee.rates = normalized.components.energyFee.rates.map((rate: any) => ({
                ...rate,
                timeWindows: rate.timeWindows?.map((window: any) => ({
                    ...window,
                    days: window.days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                })) || []
            }));
        }

        // timeFee rates mirror energyFee: only normalise the required `days` array inside time
        // windows; per-unit pricing values are left as-is so missing/invalid rates are caught.
        if (normalized.components.timeFee?.rates) {
            normalized.components.timeFee.rates = normalized.components.timeFee.rates.map((rate: any) => ({
                ...rate,
                timeWindows: rate.timeWindows?.map((window: any) => ({
                    ...window,
                    days: window.days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                })) || []
            }));
        }

        return normalized;
    };

    const feeStructure = normalizeFeeStructure(value);

    const [expandedPanel, setExpandedPanel] = useState<string | false>('session');

    const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const updateComponent = (componentKey: string, updates: any) => {
        const newValue = {
            ...feeStructure,
            components: {
                ...feeStructure.components,
                [componentKey]: {
                    ...feeStructure.components[componentKey as keyof typeof feeStructure.components],
                    ...updates,
                },
            },
        };
        onChange(normalizeFeeStructure(newValue));
    };

    const addEnergyRate = () => {
        const newRate: EnergyRate = {
            name: DEFAULT_RATE_TYPE, // Use default rate type instead of generic name
            rate: 1,
            unit: 'kWh',
            timeWindows: [],
        };
        
        updateComponent('energyFee', {
            rates: [...(feeStructure.components.energyFee?.rates || []), newRate],
        });
    };

    const updateEnergyRate = (index: number, updates: Partial<EnergyRate>) => {
        const rates = [...(feeStructure.components.energyFee?.rates || [])];
        rates[index] = { ...rates[index], ...updates };
        updateComponent('energyFee', { rates });
    };

    const removeEnergyRate = (index: number) => {
        const rates = [...(feeStructure.components.energyFee?.rates || [])];
        rates.splice(index, 1);
        updateComponent('energyFee', { rates });
    };

    // Update SEVERAL components in one onChange (updateComponent only touches one, so calling it
    // twice in a row would use stale state for the second call). Used by the Energy-XOR-Time
    // toggles, which must flip two components atomically.
    const updateComponents = (updatesByKey: Record<string, any>) => {
        const newComponents: any = { ...feeStructure.components };
        for (const [key, updates] of Object.entries(updatesByKey)) {
            newComponents[key] = { ...newComponents[key], ...updates };
        }
        onChange(normalizeFeeStructure({ ...feeStructure, components: newComponents }));
    };

    const toggleEnergyFee = (enabled: boolean) => {
        updateComponents(computeEnergyToggle(enabled));
    };

    const toggleTimeFee = (enabled: boolean) => {
        updateComponents(computeTimeToggle(enabled));
    };

    const addTimeRate = () => {
        const newRate: TimeRate = {
            name: DEFAULT_RATE_TYPE,
            rate: 1,
            timeWindows: [],
        };
        updateComponent('timeFee', {
            rates: [...(feeStructure.components.timeFee?.rates || []), newRate],
        });
    };

    const updateTimeRate = (index: number, updates: Partial<TimeRate>) => {
        const rates = [...(feeStructure.components.timeFee?.rates || [])];
        rates[index] = { ...rates[index], ...updates };
        updateComponent('timeFee', { rates });
    };

    const removeTimeRate = (index: number) => {
        const rates = [...(feeStructure.components.timeFee?.rates || [])];
        rates.splice(index, 1);
        updateComponent('timeFee', { rates });
    };

    // Legacy templates may have BOTH energy and time enabled (created before the XOR rule). We do
    // NOT silently mutate them; the form shows a non-blocking warning and the validator blocks save.
    const energyAndTimeBothOn =
        feeStructure.components.energyFee?.enabled === true &&
        feeStructure.components.timeFee?.enabled === true;

    const componentIcons = {
        session: <AttachMoney />,
        energy: <ElectricBolt />,
        time: <Schedule />,
        idle: <HourglassEmpty />,
        reservation: <EventSeat />,
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Session Fee */}
            <Accordion 
                expanded={expandedPanel === 'session'} 
                onChange={handlePanelChange('session')}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {componentIcons.session}
                        <Typography>{translate('resources.fee-templates.components.sessionFee')}</Typography>
                        {feeStructure.components.sessionFee?.enabled && (
                            <Chip label={translate('resources.fee-templates.components.enabled')} size="small" color="primary" />
                        )}
                        <Box sx={{ ml: 'auto' }}>
                            <Tooltip title={translate('resources.fee-templates.tooltips.sessionFee')} arrow>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0.5,
                                        cursor: 'help'
                                    }}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <InfoOutlined fontSize="small" />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={feeStructure.components.sessionFee?.enabled || false}
                                    onChange={(e) => updateComponent('sessionFee', { enabled: e.target.checked })}
                                />
                            }
                            label={translate('resources.fee-templates.components.enable')}
                        />
                        
                        {feeStructure.components.sessionFee?.enabled && (
                            <>
                                <NumberField
                                    label={translate('resources.fee-templates.fields.oneOffAmount')}
                                    value={feeStructure.components.sessionFee.amount}
                                    onValueChange={(v) => updateComponent('sessionFee', { amount: v })}
                                    InputProps={{
                                        inputProps: { min: 0, step: 0.01 },
                                        endAdornment: <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}>HKD</Box>
                                    }}
                                    helperText={translate('resources.fee-templates.sessionFee.amountHelper')}
                                />
                                <TextField
                                    label={translate('resources.fee-templates.fields.description')}
                                    value={feeStructure.components.sessionFee.description || ''}
                                    onChange={(e) => updateComponent('sessionFee', { description: e.target.value })}
                                    multiline
                                    rows={2}
                                />
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Energy Fee */}
            <Accordion 
                expanded={expandedPanel === 'energy'} 
                onChange={handlePanelChange('energy')}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {componentIcons.energy}
                        <Typography>{translate('resources.fee-templates.components.energyFee')}</Typography>
                        {feeStructure.components.energyFee?.enabled && (
                            <Chip label={translate('resources.fee-templates.components.enabled')} size="small" color="primary" />
                        )}
                        <Box sx={{ ml: 'auto' }}>
                            <Tooltip title={translate('resources.fee-templates.tooltips.energyFee')} arrow>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0.5,
                                        cursor: 'help'
                                    }}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <InfoOutlined fontSize="small" />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={feeStructure.components.energyFee?.enabled || false}
                                    onChange={(e) => toggleEnergyFee(e.target.checked)}
                                    disabled={feeStructure.components.timeFee?.enabled === true && feeStructure.components.energyFee?.enabled !== true}
                                />
                            }
                            label={translate('resources.fee-templates.components.enable')}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.fee-templates.components.energyTimeExclusiveHelp')}
                        </Typography>
                        {energyAndTimeBothOn && (
                            <Alert severity="warning">
                                {translate('resources.fee-templates.validation.energyTimeMutuallyExclusive')}
                            </Alert>
                        )}

                        {feeStructure.components.energyFee?.enabled && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">
                                        {translate('resources.fee-templates.components.energyRates')}
                                    </Typography>
                                    <Button
                                        startIcon={<Add />}
                                        onClick={addEnergyRate}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {translate('resources.fee-templates.actions.addRate')}
                                    </Button>
                                </Box>
                                
                                {feeStructure.components.energyFee?.rates?.map((rate: any, index: number) => (
                                    <Card key={index} variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, mb: 3 }}>
                                                <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                                                    <InputLabel>{translate('resources.fee-templates.fields.rateName')}</InputLabel>
                                                    <Select
                                                        value={rate.name || DEFAULT_RATE_TYPE}
                                                        onChange={(e) => updateEnergyRate(index, { name: e.target.value })}
                                                        label={translate('resources.fee-templates.fields.rateName')}
                                                    >
                                                        {RATE_TYPE_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {translate(option.label)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                                                        {translate('resources.fee-templates.energyFee.nameHelper')}
                                                    </Typography>
                                                </FormControl>
                                                <NumberField
                                                    label={translate('resources.fee-templates.fields.pricePerKwh')}
                                                    value={rate.rate}
                                                    onValueChange={(v) => updateEnergyRate(index, { rate: v })}
                                                    InputProps={{
                                                        inputProps: { min: 0, step: 0.01 },
                                                        endAdornment: <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}>HKD/kWh</Box>
                                                    }}
                                                    helperText={translate('resources.fee-templates.energyFee.rateHelper')}
                                                    sx={{ width: { xs: '100%', sm: 180 } }}
                                                />
                                                <Button
                                                    color="error"
                                                    onClick={() => removeEnergyRate(index)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, flexShrink: 0 }}
                                                >
                                                    {translate('ra.action.delete')}
                                                </Button>
                                            </Box>
                                            
                                            {/* Time Window Configuration */}
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Schedule fontSize="small" />
                                                    {translate('resources.fee-templates.energyFee.timeRange')}
                                                </Typography>
                                                
                                                {(!rate.timeWindows || rate.timeWindows.length === 0) ? (
                                                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Schedule fontSize="small" />
                                                            {translate('resources.fee-templates.energyFee.appliesAllTime')}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {translate('resources.fee-templates.energyFee.allTimeHelper')}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ mb: 2 }}>
                                                        {rate.timeWindows.map((window: any, windowIndex: number) => (
                                                            <Box key={windowIndex} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 2 } }}>
                                                                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 3 }, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                                                                    <CustomTimePicker
                                                                        label={translate('resources.fee-templates.energyFee.startTime')}
                                                                        value={window.start || '09:00'}
                                                                        onChange={(value) => {
                                                                            const updatedWindows = [...(rate.timeWindows || [])];
                                                                            updatedWindows[windowIndex] = {
                                                                                ...window,
                                                                                start: value,
                                                                                days: window.days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                                                                            };
                                                                            updateEnergyRate(index, { ...rate, timeWindows: updatedWindows });
                                                                        }}
                                                                    />
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mx: { xs: 0, sm: 1 }, mb: 1 }}>to</Typography>
                                                                    <CustomTimePicker
                                                                        label={translate('resources.fee-templates.energyFee.endTime')}
                                                                        value={window.end || '17:00'}
                                                                        onChange={(value) => {
                                                                            const updatedWindows = [...(rate.timeWindows || [])];
                                                                            updatedWindows[windowIndex] = {
                                                                                ...window,
                                                                                end: value,
                                                                                days: window.days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
                                                                            };
                                                                            updateEnergyRate(index, { ...rate, timeWindows: updatedWindows });
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Button
                                                                    color="error"
                                                                    onClick={() => {
                                                                        const updatedWindows = rate.timeWindows.filter((_: any, i: number) => i !== windowIndex);
                                                                        updateEnergyRate(index, { ...rate, timeWindows: updatedWindows });
                                                                    }}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, flexShrink: 0 }}
                                                                >
                                                                    {translate('ra.action.delete')}
                                                                </Button>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                
                                                <Box sx={{ mb: 2 }}>
                                                    <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                                        {translate('resources.fee-templates.energyFee.timeRangeHelper')}
                                                    </Alert>
                                                </Box>
                                                
                                                <Button
                                                    startIcon={<Add />}
                                                    onClick={() => {
                                                        const newTimeWindow = { 
                                                            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
                                                            start: '08:00', 
                                                            end: '17:59' 
                                                        };
                                                        const updatedRate = { 
                                                            ...rate, 
                                                            timeWindows: [...(rate.timeWindows || []), newTimeWindow]
                                                        };
                                                        updateEnergyRate(index, updatedRate);
                                                    }}
                                                    size="small"
                                                    variant="outlined"
                                                >
                                                    {translate('resources.fee-templates.energyFee.addTimeRange')}
                                                </Button>
                                            </Box>
                                            
                                            <Alert severity="info" sx={{ mt: 2 }}>
                                                {translate('resources.fee-templates.energyFee.explanation')}
                                            </Alert>
                                        </CardContent>
                                    </Card>
                                ))}
                                
                                {(!feeStructure.components.energyFee?.rates || feeStructure.components.energyFee.rates.length === 0) && (
                                    <Alert severity="warning">
                                        {translate('resources.fee-templates.components.noRates')}
                                    </Alert>
                                )}
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Time Fee */}
            <Accordion 
                expanded={expandedPanel === 'time'} 
                onChange={handlePanelChange('time')}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {componentIcons.time}
                        <Typography>{translate('resources.fee-templates.components.timeFee')}</Typography>
                        {feeStructure.components.timeFee?.enabled && (
                            <Chip label={translate('resources.fee-templates.components.enabled')} size="small" color="primary" />
                        )}
                        <Box sx={{ ml: 'auto' }}>
                            <Tooltip title={translate('resources.fee-templates.tooltips.timeFee')} arrow>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0.5,
                                        cursor: 'help'
                                    }}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <InfoOutlined fontSize="small" />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={feeStructure.components.timeFee?.enabled || false}
                                    onChange={(e) => toggleTimeFee(e.target.checked)}
                                    disabled={feeStructure.components.energyFee?.enabled === true && feeStructure.components.timeFee?.enabled !== true}
                                />
                            }
                            label={translate('resources.fee-templates.components.enable')}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.fee-templates.components.energyTimeExclusiveHelp')}
                        </Typography>
                        {energyAndTimeBothOn && (
                            <Alert severity="warning">
                                {translate('resources.fee-templates.validation.energyTimeMutuallyExclusive')}
                            </Alert>
                        )}

                        {feeStructure.components.timeFee?.enabled && (
                            <>
                                {/* Unit selector — drives the ceiling granularity (REF-A §A.1) — and free minutes */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                                        <InputLabel>{translate('resources.fee-templates.fields.timeUnit')}</InputLabel>
                                        <Select
                                            value={feeStructure.components.timeFee.unit || 'minute'}
                                            onChange={(e) => updateComponent('timeFee', { unit: e.target.value })}
                                            label={translate('resources.fee-templates.fields.timeUnit')}
                                        >
                                            <MenuItem value="minute">{translate('resources.fee-templates.fields.perMinute')}</MenuItem>
                                            <MenuItem value="hour">{translate('resources.fee-templates.fields.perHour')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <NumberField
                                        label={translate('resources.fee-templates.fields.freeMinutes')}
                                        value={feeStructure.components.timeFee.freeMinutes}
                                        onValueChange={(v) => updateComponent('timeFee', { freeMinutes: v })}
                                        integer
                                        InputProps={{ inputProps: { min: 0 } }}
                                        sx={{ width: { xs: '100%', sm: 180 } }}
                                    />
                                </Box>

                                {/* Windowed per-unit rates (mirrors energyFee) */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">
                                        {translate('resources.fee-templates.components.timeRates')}
                                    </Typography>
                                    <Button startIcon={<Add />} onClick={addTimeRate} variant="outlined" size="small">
                                        {translate('resources.fee-templates.actions.addRate')}
                                    </Button>
                                </Box>

                                {feeStructure.components.timeFee?.rates?.map((rate: any, index: number) => (
                                    <Card key={index} variant="outlined">
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, mb: 3 }}>
                                                <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                                                    <InputLabel>{translate('resources.fee-templates.fields.rateName')}</InputLabel>
                                                    <Select
                                                        value={rate.name || DEFAULT_RATE_TYPE}
                                                        onChange={(e) => updateTimeRate(index, { name: e.target.value })}
                                                        label={translate('resources.fee-templates.fields.rateName')}
                                                    >
                                                        {RATE_TYPE_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {translate(option.label)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <NumberField
                                                    label={translate('resources.fee-templates.fields.ratePerUnit')}
                                                    value={rate.rate}
                                                    onValueChange={(v) => updateTimeRate(index, { rate: v })}
                                                    InputProps={{
                                                        inputProps: { min: 0, step: 0.01 },
                                                        endAdornment: <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}>{feeStructure.components.timeFee.unit === 'hour' ? 'HKD/hr' : 'HKD/min'}</Box>
                                                    }}
                                                    helperText={translate('resources.fee-templates.timeFee.rateHelper')}
                                                    sx={{ width: { xs: '100%', sm: 180 } }}
                                                />
                                                <Button color="error" onClick={() => removeTimeRate(index)} size="small" variant="outlined" sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, flexShrink: 0 }}>
                                                    {translate('ra.action.delete')}
                                                </Button>
                                            </Box>

                                            {/* Time windows (optional; no windows = applies all the time) */}
                                            <Box sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Schedule fontSize="small" />
                                                    {translate('resources.fee-templates.energyFee.timeRange')}
                                                </Typography>
                                                {(!rate.timeWindows || rate.timeWindows.length === 0) ? (
                                                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
                                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Schedule fontSize="small" />
                                                            {translate('resources.fee-templates.energyFee.appliesAllTime')}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ mb: 2 }}>
                                                        {rate.timeWindows.map((window: any, windowIndex: number) => (
                                                            <Box key={windowIndex} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 2 } }}>
                                                                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 3 }, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                                                                    <CustomTimePicker
                                                                        label={translate('resources.fee-templates.energyFee.startTime')}
                                                                        value={window.start || '09:00'}
                                                                        onChange={(value) => {
                                                                            const updatedWindows = [...(rate.timeWindows || [])];
                                                                            updatedWindows[windowIndex] = { ...window, start: value, days: window.days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] };
                                                                            updateTimeRate(index, { ...rate, timeWindows: updatedWindows });
                                                                        }}
                                                                    />
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mx: { xs: 0, sm: 1 }, mb: 1 }}>to</Typography>
                                                                    <CustomTimePicker
                                                                        label={translate('resources.fee-templates.energyFee.endTime')}
                                                                        value={window.end || '17:00'}
                                                                        onChange={(value) => {
                                                                            const updatedWindows = [...(rate.timeWindows || [])];
                                                                            updatedWindows[windowIndex] = { ...window, end: value, days: window.days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] };
                                                                            updateTimeRate(index, { ...rate, timeWindows: updatedWindows });
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Button color="error" onClick={() => {
                                                                    const updatedWindows = rate.timeWindows.filter((_: any, i: number) => i !== windowIndex);
                                                                    updateTimeRate(index, { ...rate, timeWindows: updatedWindows });
                                                                }} size="small" variant="outlined" sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, flexShrink: 0 }}>
                                                                    {translate('ra.action.delete')}
                                                                </Button>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                <Button startIcon={<Add />} onClick={() => {
                                                    const newTimeWindow = { days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'], start: '08:00', end: '17:59' };
                                                    updateTimeRate(index, { ...rate, timeWindows: [...(rate.timeWindows || []), newTimeWindow] });
                                                }} size="small" variant="outlined">
                                                    {translate('resources.fee-templates.energyFee.addTimeRange')}
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}

                                {(!feeStructure.components.timeFee?.rates || feeStructure.components.timeFee.rates.length === 0) && (
                                    <Alert severity="warning">
                                        {translate('resources.fee-templates.components.noRates')}
                                    </Alert>
                                )}

                                {/* Max charging time (auto-stop) + unlimited pre-auth */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <NumberField
                                        label={translate('resources.fee-templates.fields.maxChargeMinutes')}
                                        value={feeStructure.components.timeFee.maxChargeMinutes}
                                        onValueChange={(v) => updateComponent('timeFee', { maxChargeMinutes: v })}
                                        integer
                                        InputProps={{ inputProps: { min: 0 } }}
                                        helperText={translate('resources.fee-templates.timeFee.maxChargeMinutesHelper')}
                                        sx={{ flex: 1 }}
                                    />
                                    {(!feeStructure.components.timeFee.maxChargeMinutes || feeStructure.components.timeFee.maxChargeMinutes === 0) && (
                                        <NumberField
                                            label={translate('resources.fee-templates.fields.noLimitPreAuthAmount')}
                                            value={feeStructure.components.timeFee.noLimitPreAuthAmount}
                                            onValueChange={(v) => updateComponent('timeFee', { noLimitPreAuthAmount: v })}
                                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                            helperText={translate('resources.fee-templates.timeFee.preAuthHelper')}
                                            sx={{ flex: 1 }}
                                        />
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Idle Fee */}
            <Accordion 
                expanded={expandedPanel === 'idle'} 
                onChange={handlePanelChange('idle')}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {componentIcons.idle}
                        <Typography>{translate('resources.fee-templates.components.idleFee')}</Typography>
                        {feeStructure.components.idleFee?.enabled && (
                            <Chip label={translate('resources.fee-templates.components.enabled')} size="small" color="primary" />
                        )}
                        <Box sx={{ ml: 'auto' }}>
                            <Tooltip title={translate('resources.fee-templates.tooltips.idleFee')} arrow>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0.5,
                                        cursor: 'help'
                                    }}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <InfoOutlined fontSize="small" />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={feeStructure.components.idleFee?.enabled || false}
                                    onChange={(e) => updateComponent('idleFee', { enabled: e.target.checked })}
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    {translate('resources.fee-templates.components.enable')}
                                    <Tooltip
                                        title={translate('resources.fee-templates.tooltips.idleFeeEnabledDisabled')}
                                        arrow
                                    >
                                        <InfoOutlined fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                                    </Tooltip>
                                </Box>
                            }
                        />

                        {feeStructure.components.idleFee?.enabled && (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <NumberField
                                        label={translate('resources.fee-templates.fields.graceMinutes')}
                                        value={feeStructure.components.idleFee.graceMinutes}
                                        onValueChange={(v) => updateComponent('idleFee', { graceMinutes: v })}
                                        integer
                                        InputProps={{ inputProps: { min: 0 } }}
                                        error={!!idleGraceErrorText}
                                        helperText={idleGraceErrorText}
                                        sx={{ flex: 1 }}
                                    />
                                    <NumberField
                                        label={translate('resources.fee-templates.fields.ratePerMinute')}
                                        value={feeStructure.components.idleFee.rate}
                                        onValueChange={(v) => updateComponent('idleFee', { rate: v })}
                                        InputProps={{
                                            inputProps: { min: 0, step: 0.01 },
                                            endAdornment: <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}>HKD/min</Box>
                                        }}
                                        error={!!idleRateErrorText}
                                        helperText={idleRateErrorText || translate('resources.fee-templates.idleFee.rateHelper')}
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                                <NumberField
                                    label={translate('resources.fee-templates.fields.maxPerSession')}
                                    value={feeStructure.components.idleFee.maxPerSession}
                                    onValueChange={(v) => updateComponent('idleFee', { maxPerSession: v })}
                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                    helperText={translate('resources.fee-templates.fields.maxPerSessionHelper')}
                                />
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Reservation Fee */}
            <Accordion 
                expanded={expandedPanel === 'reservation'} 
                onChange={handlePanelChange('reservation')}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        {componentIcons.reservation}
                        <Typography>{translate('resources.fee-templates.components.reservationFee')}</Typography>
                        {feeStructure.components.reservationFee?.enabled && (
                            <Chip label={translate('resources.fee-templates.components.enabled')} size="small" color="primary" />
                        )}
                        <Box sx={{ ml: 'auto' }}>
                            <Tooltip title={translate('resources.fee-templates.tooltips.reservationFee')} arrow>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0.5,
                                        cursor: 'help'
                                    }}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    <InfoOutlined fontSize="small" />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={feeStructure.components.reservationFee?.enabled || false}
                                    onChange={(e) => updateComponent('reservationFee', { enabled: e.target.checked })}
                                />
                            }
                            label={translate('resources.fee-templates.components.enable')}
                        />
                        
                        {feeStructure.components.reservationFee?.enabled && (
                            <>
                                <NumberField
                                    label={translate('resources.fee-templates.fields.oneOffAmount')}
                                    value={feeStructure.components.reservationFee.amount}
                                    onValueChange={(v) => updateComponent('reservationFee', { amount: v })}
                                    InputProps={{
                                        inputProps: { min: 0, step: 0.01 },
                                        endAdornment: <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', ml: 1 }}>HKD</Box>
                                    }}
                                    helperText={translate('resources.fee-templates.reservationFee.amountHelper')}
                                />
                                {Number(feeStructure.components.reservationFee.amount) === 0 && (
                                    <Alert severity="info" sx={{ mt: -1 }}>
                                        {translate('resources.fee-templates.reservationFee.zeroAmountNote')}
                                    </Alert>
                                )}
                                <PolicyDropdown
                                    value={feeStructure.components.reservationFee.policyId ?? null}
                                    onChange={(id) => updateComponent('reservationFee', { policyId: id })}
                                />
                                <RefundLabelPreview policyId={feeStructure.components.reservationFee.policyId ?? null} />
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};